import {SectionBgView} from '../ui_canvas_engine/SectionBgView'
import {GameStateEnums} from '../../../../shared/GameEnums'
import {UIGameManager} from './UIGameManager'
import {UIGameMechanic} from './UIGameEventController'
import {TimeUtils} from '../../../../shared/utils/TimeUtils'
import {UIGamePlayerAvatarView} from './ui_game_players/UIGamePlayerAvatarView'
import {TextTruncatedView} from '../ui_canvas_engine/TextTruncatedView'
import {TranslationObject} from '@colonist/i18n'
import {UIUtilsEjsVariables} from '../UIUtilsEjsVariables'
import {ActionState, PlayerColor, TurnState} from '@colonist/game-states'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums
import {SpriteView} from '../ui_canvas_engine/SpriteView'
import {ArrayUtils} from '@colonist/utils'
import {CurrentStateMessageInput, CurrentStateMessageUtils} from '@colonist/ui-game'

export abstract class UIGameStateController implements UIGameMechanic {
    protected readonly uiGameManager: UIGameManager
    protected container: SectionBgView

    protected stateText: TextTruncatedView
    protected singlePlayerAvatar: UIGamePlayerAvatarView
    protected multiPlayerIcon: SpriteView

    constructor(uiGameManager: UIGameManager) {
        this.uiGameManager = uiGameManager
        this.uiGameManager.eventController.registerUIGameMechanic(this)

        this.createContainer()
        this.connectedToGame()
    }

    protected abstract createContainer()

    onEvent(data: any, eventType: UIGameEventEnums) {
        switch(eventType) {
            case UIGameEventEnums.PlayerControllerStateUpdated:
            case UIGameEventEnums.TradeStateUpdated:
            case UIGameEventEnums.GameStateUpdated: this.update(); break
            case UIGameEventEnums.CanvasResized: this.onCanvasResize(); break
            case UIGameEventEnums.BeginnerModeStart: this.onBeginnerModeStart(); break
            default: break
        }
    }

    private onCanvasResize() {
        if(!this.container.visible) return
        this.container.destroy()
        this.singlePlayerAvatar.destroy()
        this.stateText.destroy()
        this.createContainer()
        this.update()
    }

    private onBeginnerModeStart() {
        this.container.visible = false
    }

    private connectedToGame() {
        if(this.uiGameManager.gameController.isReplay) {
            this.update()
            return
        }

        this.updateText({key: 'strings:game.gameState.connectedToGame'})
        this.showSinglePlayerAvatar(this.uiGameManager.gameController.myColor)
        const delayBeforeDisplayingTurnState = 3 * TimeUtils.MILLISECONDS
        setTimeout(() => {
            this.update()
        }, delayBeforeDisplayingTurnState)
    }

    private update() {
        const amITakingAction = this.uiGameManager.gameController.amITakingAction()
        const playersTakingAction = this.uiGameManager.gameController.getPlayersTakingAction()
        this.handleUpdatePlayerAvatar(amITakingAction, playersTakingAction)
        this.updateGameStateMessage(amITakingAction, playersTakingAction)
    }

    private handleUpdatePlayerAvatar(amITakingAction: boolean, playersTakingAction: PlayerColor[]) {
        const amICurrentTurnPlayer = this.uiGameManager.gameController.currentState.currentTurnPlayerColor == this.uiGameManager.gameController.myColor
        const wouldTradesAffectCurrentStateDisplay = amICurrentTurnPlayer && this.uiGameManager.gameController.currentState.turnState == TurnState.Turn

        wouldTradesAffectCurrentStateDisplay ? this.updatePlayerAvatarForOngoingTrade(playersTakingAction) : this.updatePlayerAvatar(amITakingAction, playersTakingAction)
    }

    private updatePlayerAvatarForOngoingTrade(playersTakingAction: PlayerColor[]) {
        const wasATradeAccepted = this.uiGameManager.gameController.wasATradeAccepted()
        const currentTurnPlayerColor = this.uiGameManager.gameController.currentState.currentTurnPlayerColor

        if(wasATradeAccepted) return this.showSinglePlayerAvatar(currentTurnPlayerColor)

        const otherPlayersTrading = [...playersTakingAction]
        ArrayUtils.removeElementFromArray(otherPlayersTrading, currentTurnPlayerColor)
        if(otherPlayersTrading.length == 0) return this.showSinglePlayerAvatar(currentTurnPlayerColor)
        if(otherPlayersTrading.length == 1) return this.showSinglePlayerAvatar(otherPlayersTrading[0])
        this.showMultiplayerIcon()
    }

    private updatePlayerAvatar(amITakingAction: boolean, playersTakingAction: PlayerColor[]) {
        if(playersTakingAction.length == 0) return this.showSinglePlayerAvatar(this.uiGameManager.gameController.currentState.currentTurnPlayerColor)
        if(playersTakingAction.length == 1) return this.showSinglePlayerAvatar(playersTakingAction[0])

        amITakingAction ? this.showSinglePlayerAvatar(this.uiGameManager.gameController.myColor) : this.showMultiplayerIcon()
    }

    private showMultiplayerIcon() {
        if(this.singlePlayerAvatar.parent != undefined) this.singlePlayerAvatar.parent.removeChild(this.singlePlayerAvatar)
        this.container.addChild(this.multiPlayerIcon)
    }

    private showSinglePlayerAvatar(player: PlayerColor) {
        const playerState = this.uiGameManager.gameController.getPlayerState(player)
        if(this.multiPlayerIcon.parent != undefined) this.multiPlayerIcon.parent.removeChild(this.multiPlayerIcon)
        this.container.addChild(this.singlePlayerAvatar)
        void this.singlePlayerAvatar.updatePlayerColor(playerState)
    }

    private updateGameStateMessage(amITakingAction: boolean, playersTakingAction: PlayerColor[]) {
        const isActionStateNone = this.uiGameManager.gameController.currentState.actionState == ActionState.None

        const playerDisplayUsername = this.getPlayerDisplayUsername(playersTakingAction)
        let message: TranslationObject
        const turnState = this.uiGameManager.gameController.currentState.turnState

        if(!isActionStateNone) {
            const currentStateMessageInput: CurrentStateMessageInput = {
                type: 'action-state',
                currentState: this.uiGameManager.gameController.currentState,
                playersTakingAction: playersTakingAction,
                myColor: this.uiGameManager.gameController.myColor,
                gameModeData: this.uiGameManager.gameState.gameModeData,
                displayUsername: playerDisplayUsername,
            }
            message = CurrentStateMessageUtils.getCurrentStateMessage(currentStateMessageInput)
            this.updateText(message)
            return
        }

        let turnStateMessageCalculationInput: CurrentStateMessageInput

        if(turnState == TurnState.Turn) {
            const otherPlayersTrading = [...playersTakingAction]
            ArrayUtils.removeElementFromArray(otherPlayersTrading, this.uiGameManager.gameController.currentState.currentTurnPlayerColor)
            const otherPlayerDisplayName = this.getPlayerDisplayUsername(otherPlayersTrading)

            turnStateMessageCalculationInput = {
                amITakingAction: amITakingAction,
                type: 'turn-state-turn',
                currentState: this.uiGameManager.gameController.currentState,
                playersTakingAction: playersTakingAction,
                playerDisplayUsername: playerDisplayUsername,
                numberOfPlayersTakingAction: playersTakingAction.length,
                arePlayersTrading: Object.keys(this.uiGameManager.gameController.tradeState.activeOffers).length > 0,
                myColor: this.uiGameManager.gameController.myColor,
                wasATradeAccepted: this.uiGameManager.gameController.wasATradeAccepted(),
                otherPlayerDisplayName: otherPlayerDisplayName,
            }
        } else {
            turnStateMessageCalculationInput = {
                amITakingAction: amITakingAction,
                type: 'turn-state-default',
                currentState: this.uiGameManager.gameController.currentState,
                playerDisplayUsername: playerDisplayUsername,
            }
        }

        message = CurrentStateMessageUtils.getCurrentStateMessage(turnStateMessageCalculationInput)
        this.updateText(message)
    }

    protected updateText(text: TranslationObject) {
        const isDiscord = UIUtilsEjsVariables.isUserOnDiscord
        const options = isDiscord ? {...text.options, interpolation: {escapeValue: false}} : text.options
        const message: TranslationObject = {key: text.key, options: options}
        this.stateText.setText(message)
        this.stateText.centerX()
    }

    private getPlayerDisplayUsername(playersTakingAction: PlayerColor[]): string {
        if(playersTakingAction.length == 0) {
            const currentTurnPlayer = this.uiGameManager.gameController.getPlayerState(this.uiGameManager.gameController.currentState.currentTurnPlayerColor)
            return currentTurnPlayer.username
        }

        if(playersTakingAction.length == 1) {
            const playerState = this.uiGameManager.gameController.getPlayerState(playersTakingAction[0])
            return playerState.username
        }

        return 'Players'
    }
}
